import type { NostrEvent, NostrFilter, RelayEventResult } from "./nostr";

export const PROBLEM_KIND = 31971;
/** How many kind 31971 events a root-discovery sweep asks for per relay. */
export const ROOT_DISCOVERY_LIMIT = 500;
/** The problem tree grown on boot: the AI-marketplace problem, owned by the
 * `red napplets` publishing key. */
export const ROOT_A_TAG = "31971:4bf150c3df7c821233a3525e2039cebaafa63504f4644304fc507a2cf0f121dd:6181e56573a644d58a9ac41109516bc4e21b9b98a3aac3799d30f57355300885";
export const COORDINATE = /^31971:([0-9a-f]{64}):([0-9a-f]{64})$/;

export type ProblemStatus = "draft" | "rfm" | "big" | "children" | "open" | "claimed" | "patched" | "closed";

export interface ProblemNode {
  coordinate: string;
  problemId: string;
  title: string;
  status: ProblemStatus;
  revisionId: string;
  revisionCount: number;
  forkCount: number;
  parentCoordinates: string[];
}

export interface ProblemDag {
  rootCoordinate: string;
  nodes: Map<string, ProblemNode>;
  children: Map<string, string[]>;
}

export interface StatusRollup {
  open: number;
  claimed: number;
}

const findTag = (event: NostrEvent, name: string, marker?: string) =>
  event.tags.find((item) => item[0] === name && (marker === undefined || item[3] === marker));

const tagValue = (event: NostrEvent, name: string, marker?: string) => findTag(event, name, marker)?.[1];

const coordinateOf = (event: NostrEvent) => tagValue(event, "a", "origin") ?? "";

const eligibleHead = (event: NostrEvent): boolean => {
  const owner = coordinateOf(event).split(":")[1] ?? "";
  return event.pubkey === owner || event.tags.some((item) =>
    item[0] === "p" && item[1] === event.pubkey && (item[3] === "maintainer" || item[3] === undefined));
};

const statusOf = (event: NostrEvent): ProblemStatus => {
  const value = tagValue(event, "status");
  return ["draft", "rfm", "big", "children", "open", "claimed", "patched", "closed"].includes(value ?? "")
    ? value as ProblemStatus
    : "open";
};

const parentCoordinatesOf = (event: NostrEvent) => Array.from(new Set(
  event.tags
    .filter((item) => item[0] === "a" && item[3] !== "origin" && COORDINATE.test(item[1] ?? ""))
    .map((item) => item[1])
));

/** Filters for the whole problem tree below `rootCoordinate` (NIP-1971 `A` tag). */
export function problemTreeFilters(rootCoordinate: string): NostrFilter[] {
  return [{ kinds: [PROBLEM_KIND], "#A": [rootCoordinate] }];
}

/** Filter for browsing every published problem tree root. */
export function problemRootFilters(): NostrFilter[] {
  return [{ kinds: [PROBLEM_KIND], limit: ROOT_DISCOVERY_LIMIT }];
}

export function assertRootCoordinate(value: string): string {
  const coordinate = value.trim().toLowerCase();
  if (!COORDINATE.test(coordinate)) {
    throw new Error("Enter root as 31971:<64-character owner>:<64-character problem id>.");
  }
  return coordinate;
}

export function buildProblemDag(rootCoordinate: string, results: RelayEventResult[]): ProblemDag {
  const root = assertRootCoordinate(rootCoordinate);
  const uniqueEvents = new Map<string, NostrEvent>();
  for (const { event } of results) {
    if (event.kind === PROBLEM_KIND && event.id && tagValue(event, "A") === root) uniqueEvents.set(event.id, event);
  }

  const groups = new Map<string, NostrEvent[]>();
  for (const event of uniqueEvents.values()) {
    const coordinate = coordinateOf(event);
    if (!COORDINATE.test(coordinate)) continue;
    const group = groups.get(coordinate) ?? [];
    group.push(event);
    groups.set(coordinate, group);
  }
  if (!groups.has(root)) {
    const below = groups.size;
    throw new Error(below
      ? `The root problem was not found on the relays, though ${below} problem${below === 1 ? "" : "s"} below it ${below === 1 ? "was" : "were"}. The relay that hosts the root is probably offline or has purged it.`
      : "No problems were found for that root on the relays.");
  }

  const nodes = new Map<string, ProblemNode>();
  for (const [coordinate, revisions] of groups) {
    const previous = new Set(revisions.flatMap((event) => event.tags
      .filter((item) => item[0] === "e" && item[3] === "previous")
      .map((item) => item[1])));
    const heads = revisions.filter((event) => !previous.has(event.id));
    const eligible = heads.filter(eligibleHead);
    const newestTimestamp = eligible.length ? Math.max(...eligible.map((event) => event.created_at)) : undefined;
    const newest = eligible.filter((event) => event.created_at === newestTimestamp);
    if (newest.length !== 1) throw new Error(`Problem ${coordinate} has unresolved current heads.`);
    const current = newest[0];
    const parentCoordinates = parentCoordinatesOf(current).sort();
    nodes.set(coordinate, {
      coordinate,
      problemId: coordinate.split(":")[2],
      title: tagValue(current, "title")?.trim() || "Untitled problem",
      status: statusOf(current),
      revisionId: current.id,
      revisionCount: revisions.length,
      forkCount: Math.max(0, heads.length - 1),
      parentCoordinates
    });
  }

  const children = new Map<string, string[]>();
  for (const node of nodes.values()) {
    for (const parent of node.parentCoordinates) {
      if (!nodes.has(parent) || parent === node.coordinate) continue;
      const list = children.get(parent) ?? [];
      if (!list.includes(node.coordinate)) list.push(node.coordinate);
      children.set(parent, list);
    }
  }
  for (const list of children.values()) list.sort((a, b) => nodes.get(a)!.title.localeCompare(nodes.get(b)!.title));
  return { rootCoordinate: root, nodes, children };
}

export function childCoordinates(dag: ProblemDag, coordinate: string): string[] {
  return dag.children.get(coordinate) ?? [];
}

const rollupCache = new WeakMap<ProblemDag, Map<string, StatusRollup>>();

/**
 * Total open and claimed ("in progress") problems in the subtree rooted at
 * `coordinate`, including the node itself. Each descendant is counted once,
 * even when the DAG gives it multiple parents.
 */
export function subtreeStatusCounts(dag: ProblemDag, coordinate: string): StatusRollup {
  let memo = rollupCache.get(dag);
  if (!memo) {
    memo = new Map();
    rollupCache.set(dag, memo);
  }
  const cached = memo.get(coordinate);
  if (cached) return cached;
  const rollup: StatusRollup = { open: 0, claimed: 0 };
  const seen = new Set<string>();
  const visit = (current: string) => {
    if (seen.has(current)) return;
    seen.add(current);
    const node = dag.nodes.get(current);
    if (!node) return;
    if (node.status === "open") rollup.open += 1;
    if (node.status === "claimed") rollup.claimed += 1;
    for (const child of dag.children.get(current) ?? []) visit(child);
  };
  visit(coordinate);
  memo.set(coordinate, rollup);
  return rollup;
}

export interface RootSummary {
  coordinate: string;
  title: string;
  problemCount: number;
  open?: number;
  claimed?: number;
}

/**
 * Groups kind 31971 events by their graph-root `A` tag and summarizes each
 * discovered tree root for browsing. Roots with complete, well-formed data
 * get exact subtree rollups; partial roots fall back to a plain count.
 */
export function summarizeRoots(results: RelayEventResult[]): RootSummary[] {
  const byRoot = new Map<string, RelayEventResult[]>();
  for (const result of results) {
    if (result.event.kind !== PROBLEM_KIND) continue;
    const rootTag = tagValue(result.event, "A");
    if (!rootTag || !COORDINATE.test(rootTag)) continue;
    const group = byRoot.get(rootTag) ?? [];
    group.push(result);
    byRoot.set(rootTag, group);
  }
  const summaries: RootSummary[] = [];
  for (const [coordinate, group] of byRoot) {
    try {
      const dag = buildProblemDag(coordinate, group);
      const rollup = subtreeStatusCounts(dag, coordinate);
      summaries.push({
        coordinate,
        title: dag.nodes.get(coordinate)?.title ?? "Untitled problem",
        problemCount: dag.nodes.size,
        open: rollup.open,
        claimed: rollup.claimed
      });
    } catch {
      const problems = new Set<string>();
      let title = "";
      for (const { event } of group) {
        const origin = coordinateOf(event);
        if (COORDINATE.test(origin)) problems.add(origin);
        if (!title && origin === coordinate) title = tagValue(event, "title")?.trim() ?? "";
      }
      summaries.push({
        coordinate,
        title: title || `Problem tree ${coordinate.split(":")[2].slice(0, 8)}…`,
        problemCount: problems.size
      });
    }
  }
  return summaries.sort((a, b) => a.title.localeCompare(b.title));
}

export function statusLabel(status: ProblemStatus): string {
  return status === "rfm" ? "RFM" : status[0].toUpperCase() + status.slice(1);
}
